'use client';

import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'motion/react';
import { ArrowUpRight } from 'lucide-react';
import { ICONS } from '@/lib/iconRegistry';
import GlobalTitle from '@shared/GlobalTitle';
import SectionShapes from '@shared/SectionShapes';
import { Button } from "@/components/ui/button";
import Link from "next/link";

const fadeUp = {
    hidden: { opacity: 0, y: 24 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: 'easeOut' } },
};
const stagger = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.12 } },
};

function StepCard({ step, index, total, scrollYProgress }) {
    const Icon = ICONS[step.icon];

    const window = 0.24; // how long each card takes to fully appear
    const gap = (1 - window) / (total - 1);  // spacing between card start points
    const start = index * gap;
    const end = start + window;

    const y = useTransform(scrollYProgress, [start, end], [80, 0]);
    const opacity = useTransform(scrollYProgress, [start, end], [0, 1]);

    return (
        <motion.div
            style={{ y, opacity }}
            className="relative group flex gap-5 p-6 rounded-2xl bg-white border border-gray-100 hover:border-primary/20 hover:shadow-lg hover:shadow-primary/5 transition-colors duration-300 cursor-default overflow-hidden"
        >
            <span className="absolute top-4 right-4 text-4xl font-black text-gray-100 group-hover:text-primary/20 transition-colors duration-300 leading-none select-none">
                {step.number}
            </span>

            <div className="space-y-2">
                <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary transition-colors duration-300 shrink-0">
                        {Icon && (
                            <Icon
                                size={15}
                                className="text-primary group-hover:text-white transition-colors duration-300"
                            />
                        )}
                    </div>

                    <h3 className="font-bold text-gray-900 text-lg leading-tight pr-10 lg:pr-0">
                        {step.title}
                    </h3>
                </div>

                <p className="text-muted-foreground text-sm leading-relaxed">
                    {step.body}
                </p>
            </div>
        </motion.div>
    );
}

export default function ProcessSteps({ data }) {
    const sectionRef = useRef(null);
    const { scrollYProgress } = useScroll({
        target: sectionRef,
        offset: ['start 80%', 'end 60%'],
    });

    if (!data) return null;

    const total = data.steps?.length ?? 1;

    return (
        <section
            ref={sectionRef}
            className="relative bg-[#f7f8fc] py-12 md:py-20"
        >
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <SectionShapes variant="dark" />
                <div className="absolute top-0 right-0 w-[600px] h-[500px] bg-primary/5 rounded-full blur-[140px]" />
            </div>
            <div className="max-w-[1440px] mx-auto px-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-16 items-start">
                    <motion.div
                        className="lg:sticky lg:top-32 space-y-5"
                        variants={stagger}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.2 }}
                    >
                        <motion.div variants={fadeUp}>
                            <GlobalTitle
                                topText={data.topText}
                                titleText={data.titleText}
                                coloredText={data.coloredText}
                                classes="text-left"
                            />
                        </motion.div>

                        {Array.isArray(data.description) ? (
                            data.description.map((desc, i) => (
                                <motion.p key={i} variants={fadeUp} className="text-muted-foreground leading-relaxed">
                                    {desc}
                                </motion.p>
                            ))
                        ) : (
                            <motion.p variants={fadeUp} className="text-muted-foreground leading-relaxed">
                                {data.description}
                            </motion.p>
                        )}

                        {/* Quote highlight */}
                        {data.quote && (
                            <motion.div variants={fadeUp} className="group relative p-6 rounded-2xl bg-white border border-gray-100 hover:border-primary/25 hover:shadow-lg hover:shadow-primary/5 transition-all duration-300 overflow-hidden cursor-default">
                                <span className="absolute -top-2.5 left-4 text-[7rem] font-mono text-primary/8 group-hover:text-primary/20 leading-none select-none pointer-events-none transition-colors duration-300">
                                    &ldquo;
                                </span>
                                <div className="absolute top-0 left-0 h-full w-1 rounded-l-2xl transition-all duration-300" style={{ background: 'var(--grad-red)' }} />
                                <p className="relative text-[15px] font-semibold text-gray-600 group-hover:text-gray-900 leading-relaxed italic transition-colors duration-300">
                                    {data.quote}
                                </p>
                            </motion.div>
                        )}

                        {/* Button */}
                        {data.buttonUrl && (
                            <motion.div variants={fadeUp} className="btn-group">
                                <Link href={`${data.buttonUrl}`}>
                                    <Button size="lg" className="bg-red-gradient border-0 text-sm md:text-base py-5 px-4 btn-glass group cursor-pointer">
                                        <span className="relative z-10 text-sm flex items-center gap-2 text-white group-hover:text-secondary transition-colors duration-400 leading-snug">
                                            {data.buttonText}
                                            <ArrowUpRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
                                        </span>
                                    </Button>
                                </Link>
                            </motion.div>
                        )}
                    </motion.div>

                    <div className="space-y-4">
                        {data.steps?.map((step, i) => (
                            <StepCard
                                key={step.number}
                                step={step}
                                index={i}
                                total={total}
                                scrollYProgress={scrollYProgress}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
